/**
 * Subscription Service
 *
 * Handles Stripe subscription operations via the self-hosted API.
 * Provides methods for creating checkout sessions, fetching subscription status,
 * and managing subscription via Stripe Customer Portal.
 */

import { getAccessToken, isAuthConfigured } from './authService';
import { fetchEntitlementToken } from './entitlementTokenService';
import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || '';

// Price IDs from Stripe dashboard
const MONTHLY_PRICE_ID = 'price_1SqNJX7ujB0vlNcrwaweLGod';
const YEARLY_PRICE_ID = 'price_1SqNK47ujB0vlNcro1phX0Nr';

/**
 * Subscription status from Stripe API.
 * Must match the DB CHECK constraint in subscriptions.status.
 * 'none' is an app-only status indicating no subscription exists.
 */
export type SubscriptionStatus =
  | 'active'           // Active subscription
  | 'past_due'         // Payment failed but subscription not canceled
  | 'canceled'         // Subscription canceled
  | 'unpaid'           // Final invoice is past due
  | 'trialing'         // In trial period
  | 'incomplete'       // Initial payment not yet attempted/processed
  | 'incomplete_expired' // After 23 hours if initial payment failed
  | 'paused'           // Subscription paused (if pausing is enabled)
  | 'none';            // No subscription exists (app-only status)
export type PricingPlan = 'monthly' | 'yearly';

export interface Subscription {
  id: string;
  userId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string | null;
  status: SubscriptionStatus;
  priceId: string;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
}

export interface SubscriptionResult {
  success: boolean;
  subscription?: Subscription;
  error?: string;
}

export interface CheckoutResult {
  success: boolean;
  sessionId?: string;
  url?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Check if subscription service is configured.
 */
export const isSubscriptionConfigured = (): boolean => isAuthConfigured() && Boolean(API_URL);

/**
 * Get the price ID for a given plan.
 */
export const getPriceId = (plan: PricingPlan): string => {
  return plan === 'monthly' ? MONTHLY_PRICE_ID : YEARLY_PRICE_ID;
};

const EMPTY_SUBSCRIPTION: Subscription = {
  id: '',
  userId: '',
  stripeCustomerId: '',
  stripeSubscriptionId: null,
  status: 'none',
  priceId: '',
  currentPeriodStart: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
};

/**
 * Get the current user's subscription status.
 * Derives subscription data from the entitlement token endpoint
 * (the backend is the authority on subscription state).
 */
export const getSubscription = async (): Promise<SubscriptionResult> => {
  if (!API_URL) {
    return { success: false, error: 'API URL is not configured' };
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Not authenticated - no access token' };
    }

    const result = await fetchEntitlementToken();
    if (!result.success || !result.payload) {
      return { success: false, error: result.error || 'Failed to fetch entitlement' };
    }

    const { payload } = result;
    const status: SubscriptionStatus = payload.subscription_status
      ? (payload.subscription_status as SubscriptionStatus)
      : 'none';

    return {
      success: true,
      subscription: {
        ...EMPTY_SUBSCRIPTION,
        status,
        currentPeriodEnd: payload.subscription_expires_at ? new Date(payload.subscription_expires_at) : null,
      },
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch subscription',
    };
  }
};

/**
 * Create a Stripe Checkout session for subscription purchase.
 * Opens in the user's default browser.
 */
export const createCheckoutSession = async (plan: PricingPlan): Promise<CheckoutResult> => {
  if (!API_URL) {
    return { success: false, error: 'API URL is not configured' };
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Not authenticated - no access token' };
    }

    const priceId = getPriceId(plan);
    const email = useAuthStore.getState().user?.email;

    const response = await fetch(`${API_URL}/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ priceId, email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Request failed with status ${response.status}`,
        errorCode: errorData.errorCode,
      };
    }

    const { sessionId, url } = await response.json();

    return { success: true, sessionId, url };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to create checkout session',
    };
  }
};

/**
 * Get the Stripe Customer Portal URL for managing subscription.
 * Opens in the user's default browser for updating payment method, canceling, etc.
 */
export const getCustomerPortalUrl = async (): Promise<{ success: boolean; url?: string; error?: string }> => {
  if (!API_URL) {
    return { success: false, error: 'API URL is not configured' };
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Not authenticated - no access token' };
    }

    const response = await fetch(`${API_URL}/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `Request failed with status ${response.status}` };
    }

    const data = await response.json();
    return { success: true, url: data.url };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to get portal URL',
    };
  }
};

/**
 * Check if a subscription is in an active state (user has Pro access).
 */
export const isSubscriptionActive = (subscription: Subscription | null | undefined): boolean => {
  if (!subscription) return false;
  return subscription.status === 'active' || subscription.status === 'trialing';
};

/** Grace period duration in days after subscription period end */
const GRACE_PERIOD_DAYS = 7;

/** Subscription statuses that can have grace period access */
const GRACE_PERIOD_STATUSES: SubscriptionStatus[] = ['past_due', 'canceled', 'unpaid'];

/**
 * Check if a subscription is in grace period.
 * Grace period applies to: past_due, canceled, unpaid (within 7 days of period end).
 * This allows users to maintain Pro access while resolving payment issues.
 */
export const isInGracePeriod = (subscription: Subscription | null | undefined): boolean => {
  if (!subscription) return false;
  if (!GRACE_PERIOD_STATUSES.includes(subscription.status)) return false;

  const periodEnd = subscription.currentPeriodEnd;
  if (!periodEnd) return false;

  const now = new Date();
  const gracePeriodEnd = new Date(periodEnd);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);

  return now < gracePeriodEnd;
};

/**
 * Get days remaining in grace period.
 * Returns 0 if not in grace period.
 */
export const getGracePeriodDaysRemaining = (subscription: Subscription | null | undefined): number => {
  if (!subscription) return 0;
  if (!GRACE_PERIOD_STATUSES.includes(subscription.status)) return 0;

  const periodEnd = subscription.currentPeriodEnd;
  if (!periodEnd) return 0;

  const now = new Date();
  const gracePeriodEnd = new Date(periodEnd);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);

  const msRemaining = gracePeriodEnd.getTime() - now.getTime();
  return Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
};
